/**
 * 「TBM 담당자 확인」 서명칸이 **왜 비어 있나** — 판정 한 벌 + 그 문구.
 *
 * ## 배경 (결정 2026-09-08 `tbm-conductor-signature-is-not-the-author`)
 * 그 칸의 서명은 **교육 담당자(진행자)의 것뿐**이다. 종전엔 교육 세션이 없으면 작성자
 * (안전관리자) 서명이 폴백으로 들어가, 성명은 담당자인데 서명은 다른 사람인 문서가 됐다 —
 * 일어나지 않은 교육을 확인한 것으로 만드는 기록이었다. 폴백을 걷어냈으므로 그 칸은 이제
 * 정직하게 비는데, **비어 있는 이유가 넷**이고 사용자에게는 그 넷이 전혀 다른 뜻이다:
 *
 * | 상태 | 뜻 | 다음에 일어날 일 |
 * |---|---|---|
 * | `offline` | 출력물에 손서명하기로 확정된 문서 | 종이가 정본 — 전자본은 영구히 빈칸 |
 * | `awaiting-educator` | 전자서명 + 담당자 지정, 교육이 아직 안 열렸다 | 담당자가 링크로 진행하면 채워진다 |
 * | `awaiting-self` | 전자서명 + **작성자 직접 진행**, 교육이 아직 안 열렸다 | 작성자 본인이 진행하면 채워진다 |
 * | `education-unfinished` | 전자서명 + 교육이 **열렸는데 아직 안 끝났다**(`IN_PROGRESS` 회차) | 그 회차를 마치면 채워진다 — **확정하면 영영 못 채운다** |
 * | `education-unfinished-empty` | 위와 같되 그 진행 중 회차가 **전부 비어 있다** | 확정하면 그 회차들이 **제외된다**(그 자리에 잃을 것이 없다) |
 * | `education-preparing` | 위 `education-unfinished`와 같되 그 회차가 전부 **「교육 시작」 전**(담당자가 입력만 했다, F-55) | 같다 — 대가는 같고 **말만 「준비 중」**이다 |
 *
 * ## 여섯 번째 상태 — 「준비 중」 (F-55, 2026-09-13)
 * 담당자가 「교육 시작」 전에 안전조치 확인·특이사항을 입력하면 자동저장이 회차를 lazy 생성한다
 * (`IN_PROGRESS`, 시작 QR 없음 → 서버 `startedAt: null`). 활동 배지는 이것을 「교육 준비 중」으로
 * 가르는데(결정 2026-09-13 2안), 이 모듈의 사유 줄이 「교육이 아직 **진행 중**이라」로 남으면 같은
 * 문서를 두고 배지와 사유가 다른 말을 한다. 결정 문서의 지시 그대로 **문구만** 가른다 — 게이트
 * 동작(경고, 차단 아님)은 `education-unfinished`와 같다(기록이 있으니 막는 게 맞고, 확정 시 대가도
 * 같다). 판정 축은 shared `deriveTbmEducationSessionState`(배지와 **같은 함수**)이고, 빈 회차 갈래가
 * 먼저다 — 비어 있으면 시작 여부와 무관하게 제외되므로 「제외됩니다」가 더 정확한 사실이다.
 *
 * ## 네 번째 상태는 F-7이 드러냈다 (2026-09-09)
 * 종전 판정은 「세션이 1건이라도 있으면 `null`」이었고 근거는 *그 칸은 채워지거나 채워질
 * 자리가 있다* 였다. 진행 중 회차는 **아직 안 채워졌고**, F-7으로 확정본의 담당자 쓰기를 닫은
 * 뒤로는 `completeByEducatorToken`이 확정본을 거부하므로 **확정하는 순간 그 회차를 아무도
 * 마무리할 수 없다.** 「채워질 자리가 있다」가 더는 참이 아니라 상태가 하나 빠져 있었던 것이다.
 *
 * 두 사실이 이 상태를 되돌릴 수 없게 만든다(둘 다 서버에서 확인, 2026-09-09):
 * ① 제출·확정 게이트에 「진행 중 교육」 축이 없다 — `hasActiveEducationSession`은 활동 배지와
 *    재전송 가드에만 쓰인다. ② 이 화면의 확정 경로(`authorSign` → `submitReport`/`resubmit`)는
 *    **세션을 건드리지 않는다**(세션을 닫는 것은 `tbm.complete()` 하나뿐인데 COMPOSED 확정은
 *    그 경로를 타지 않는다). 그래서 그 회차는 `IN_PROGRESS`로 박제된다.
 *
 * ## 다섯 번째 상태 — 「빈 진행 중 회차」 (2026-09-09, 같은 날 후속)
 * 위 ②는 그 뒤 **바뀌었다.** 결정 2026-09-09(「확정 시 «끝나지 않은 교육 회차»를 어떻게 할
 * 것인가」, 안 D 조건부 제외)로 확정 경로가 **빈 진행 중 회차를 삭제**한다. 그래서 같은
 * `IN_PROGRESS` 회차라도 사용자에게 일어날 일이 둘로 갈린다:
 *
 * - **내용이 있으면** 남는다 → 종전 문구 그대로(마무리할 수 없다 / 서명란이 빈 채로 남는다).
 *   참석자 서명은 「나는 그 자리에 있었다」는 증빙이라 지우면 그 사람이 미교육자로 잡힌다.
 * - **전부 비어 있으면** 제외된다 → 「마무리할 수 없다」는 그 문서에 대해 **거짓**이다. 마무리할
 *   회차 자체가 사라지고, 잃는 데이터는 정의상 0이다. 그 사실을 그대로 말한다.
 *
 * 판정 축은 서버가 파생해 내려주는 `educationSessions[].isEmpty`이고, 그 값은 삭제 대상을 고르는
 * 서버 로직과 **같은 shared 술어**(`isEmptyTbmEducationSession`)의 결과다. 화면이 원자료로 다시
 * 판정하지 않는 이유가 그것이다 — 각자 재면 「제외된다고 안내했는데 실제로는 남는」 비대칭이
 * 생기고, 그건 F-7이 이미 한 번 만든 병이다.
 *
 * ## 왜 「직접 진행」을 갈랐나 (2026-09-08 별건 ⑤)
 * 종전엔 두 상태뿐이라 직접 진행 건에도 *「**담당자가** 아직 교육을 진행하지 않아…」* 가
 * 붙었다. 그 문서에는 담당자가 없다 — 작성자 본인이 진행한다(결정 2026-09-08 「안전관리자가
 * 직접 진행하는 TBM 교육」 §2·§3에서 담당자 지정의 **배타 갈래**로 확정). 화면이 없는 사람을
 * 기다린다고 말하면, 읽는 사람은 *내가 뭔가 지정을 빠뜨렸나* 로 읽는다.
 *
 * 판정 축은 **명시 컬럼 `Report.tbmSelfConducted`**다 — 부재 추론
 * (`담당자 null ∧ 토큰 0`)이 아니다. 그 결정의 「판정 축」절 그대로: 다른 이유로 토큰이 0이
 * 된 문서(담당자 탈퇴, delegate 배포)가 조용히 직접 진행으로 오인된다.
 *
 * ⚠️ 그 컬럼은 **상세 응답에만** 실린다(`findOne`은 `include`라 스칼라 전량, 목록 `findAll`은
 * select 화이트리스트라 미포함). 이 판정을 쓰는 두 자리가 모두 상세 형상이라 지금은 맞지만,
 * **목록 형상에서 부르면 `undefined`가 되어 `awaiting-educator`로 떨어진다** — 새 소비처를
 * 붙일 때 그 응답이 이 축을 싣는지 먼저 확인할 것.
 *
 * ## 왜 한 벌인가
 * 소비처가 둘이다 — 상세 chrome의 사유 한 줄, 완료 확인 모달의 경고. 두 자리가 각자
 * 판정하면 같은 문서를 두고 화면이 서로 다른 말을 한다.
 *
 * ## 왜 `lib/` 순수 모듈인가
 * 컴포넌트 쪽 모듈에 두면 그 모듈을 `vi.mock`하는 기존 spec들이 새 export를 몰라
 * `No "X" export is defined on the mock`으로 무더기로 죽는다(vitest factory mock은 부분
 * mock이 아니다). 판정도 문구도 순수 값이므로 mock 대상 밖에 둔다.
 */
import {
  deriveTbmEducationSessionState,
  formatDateISO,
  isTbmEducationPeriodEnded,
  isUnfinishedTbmEducationRound,
  type TbmDocumentStatus,
} from '@safetyhub/shared';
import type { Report } from './reports';

/** 교육 담당자 확인 서명칸이 빈 사유. 빈칸이 아니거나 물을 상황이 아니면 `null`. */
export type TbmConductorSignatureState =
  | 'offline'
  | 'awaiting-educator'
  | 'awaiting-self'
  | 'education-unfinished'
  | 'education-unfinished-empty'
  | 'education-preparing'
  | 'education-period-ended';

/**
 * 문서 데이터에서 사유를 파생한다. 재료는 넷 — 확정된 서명 방식, 교육 세션 수, **회차 상태**,
 * 진행 주체.
 *
 * - `tbmSignatureMode`가 없으면 `null`: 아직 배포 설정 전이거나 TBM이 아니다. 확정되지 않은
 *   문서에 대고 "출력물에 받으세요"라고 말할 수는 없다.
 * - `educationSessionCount`가 `undefined`면 대기를 **주장하지 않는다**:
 *   목록·옛 응답에는 없는 필드라 모르는 것이지 0이 아니다(모르는 것을 "아직 안 했다"로
 *   말하면 이미 진행한 교육을 없던 일로 만든다).
 * - 세션이 있으면 **회차 상태를 한 번 더 묻는다**: `IN_PROGRESS` 회차가 하나라도 있으면
 *   `education-unfinished`(계열), 전부 닫혔으면 `null`(그 칸은 educator 서명으로 채워졌다).
 *   종전엔 여기서 곧장 `null`이었고, 그래서 *열렸지만 안 끝난* 문서가 침묵 구간이었다.
 * - 진행 중 회차가 있으면 **그것이 비었는지를 한 번 더 묻는다**: 확정 시 일어날 일이 갈리기
 *   때문이다(제외 vs 미완료로 잔존). 갈래는 `isEmpty`가 정한다 — 아래 함수 주석 참조.
 * - `educationSessions`가 `undefined`면 진행 중이라 **주장하지 않는다**: 목록·옛 응답에 없는
 *   필드라 모르는 것이지 「없다」가 아니다(`educationSessionCount`와 같은 계약). 모를 때
 *   경고를 지어내면 정상 문서의 확정마다 거짓 경고가 붙는다.
 * - `tbmSelfConducted`는 **대기 사유를 가르기만 한다** — 대기인지 여부는 위 두 축이 정한다.
 *   오프라인 건에는 이 축을 묻지 않는다(직접 진행이어도 종이가 정본이라 문구가 같다).
 *   진행 중 상태에도 묻지 않는다: 누가 열었든 **안 끝났다는 사실과 그 대가는 같다**.
 */
export function deriveTbmConductorSignatureState(
  report: Pick<
    Report,
    | 'tbmSignatureMode'
    | 'educationSessionCount'
    | 'tbmSelfConducted'
    | 'educationSessions'
    | 'educationEndDate'
  >,
  now?: Date,
): TbmConductorSignatureState | null {
  if (report.tbmSignatureMode === 'OFFLINE') return 'offline';
  if (report.tbmSignatureMode !== 'ELECTRONIC') return null;
  if (report.educationSessionCount !== 0) {
    // `?.`는 방어가 아니라 계약이다 — 행을 모르면(`undefined`) 아무 주장도 하지 않는다.
    const inProgress = report.educationSessions?.filter((s) => s.status === 'IN_PROGRESS');
    if (!inProgress || inProgress.length === 0) return null;
    /**
     * **「제외됩니다」는 알 때만 말한다.**
     *
     * `every`는 빈 배열에서 `true`라 위에서 길이를 먼저 걸렀다 — 진행 중 회차가 하나도 없는데
     * 「전부 비었다」로 접히면 그 문서는 애초에 이 갈래에 오지 않아야 한다.
     *
     * `isEmpty`가 하나라도 `undefined`면(옛 응답·목록 형상·아직 이 필드를 안 싣는 픽스처)
     * **모르는 것**이지 「비었다」가 아니다. 모르는 것을 「빔」으로 읽으면 *실제로는 남을 회차를
     * 제외된다고 안내*하게 되고, 그건 사용자가 되돌릴 수 없는 결정을 잘못된 전제로 내리는
     * 것이다. 그래서 폴백은 종전 문구(`education-unfinished`) — 그쪽은 **어느 경우에도 참**이다
     * (진행 중 회차가 있다는 사실 자체는 확실하고, 「마무리할 수 없다」는 남는 경우에만 대가를
     * 말한다). 모를 때 더 조심스러운 쪽으로 떨어지는 것이 이 모듈의 기존 계약과도 같다.
     */
    const allEmpty = inProgress.every((s) => s.isEmpty === true);
    if (allEmpty) return 'education-unfinished-empty';
    /**
     * 「준비 중」(F-55)은 **활동 배지와 같은 함수**로 판정한다 — 진행 중 회차 전부가 「교육 시작」 전
     * (`startedAt === null`)일 때만이다. `startedAt`이 `undefined`(옛 응답)면 그 함수가 종전
     * `'in-progress'`를 주므로 여기도 종전 문구다 — 모르는 것을 「준비 중」이라 주장하지 않는다.
     */
    const allPreparing = inProgress.every((s) => deriveTbmEducationSessionState(s) === 'preparing');
    return allPreparing ? 'education-preparing' : 'education-unfinished';
  }
  /**
   * **일곱 번째 상태 — 「교육 기간 종료」**(2026-09-30, 결정 「TBM 교육 기간 만료는 «종결»이다」).
   *
   * 여기까지 왔다는 것은 세션이 **0건**이라는 뜻이다. 종전엔 그 상태를 전부 *대기*로 읽어
   * 「**아직** 교육을 진행하지 않아…」라고 말했는데, 기간이 이미 끝난 문서에 대해 그 문장은
   * **거짓**이다: 「아직」은 *이제부터 일어날 수 있다*는 약속인데 서버가 새 회차를 거부하고
   * (`assertEducationPeriodOpen`) 담당자 링크 재전송도 거부하며 크론이 토큰을 비활성화했다.
   * 화면이 일어날 수 없는 일을 기다린다고 말하면 사용자는 담당자를 재촉하거나 링크를 다시
   * 보내려 한다 — **할 수 없는 행동으로 안내하는 것**이다.
   *
   * **`tbmSelfConducted`를 묻지 않는다** — 대기 사유를 가르던 그 축은 *누가 진행할 사람인가*를
   * 답하는데, 기간이 끝나면 **아무도 진행할 수 없다.** 직접 진행 건도 같은 게이트에 막히므로
   * (F-36: `createQRSession`도 `assertEducationPeriodOpen`을 부른다) 남는 사실이 하나뿐이고,
   * 그래서 문구에도 주어를 넣지 않는다(`awaiting-self`가 주어를 뺀 것과 같은 이유의 확장).
   *
   * 위 진행 중 갈래들보다 **뒤**인 이유: 회차가 하나라도 있으면 기간이 끝났어도 사용자에게
   * 더 급한 사실은 *그 회차가 어떻게 되는가*(확정 시 잔존/제외)다. 기간 종료는 그때 배지가
   * 말한다(shared `deriveTbmEducationProgress`의 `'ended'`) — 한 화면에서 두 줄이 서로 다른
   * 층의 사실을 말하는 것이지 같은 말을 두 번 하는 것이 아니다.
   */
  if (isTbmEducationPeriodEnded(report, now)) return 'education-period-ended';
  return report.tbmSelfConducted ? 'awaiting-self' : 'awaiting-educator';
}

/**
 * 상세 chrome 한 줄 — 양식 본문은 건드리지 않는다(스냅샷 박제라 기존 문서에 안 붙는다).
 *
 * ## 왜 맵이 아니라 함수인가 (2026-09-30)
 * 종전엔 `Record<TbmConductorSignatureState, string>` 상수였고, 그 형태의 값은 **상태 하나에
 * 문자열 하나**다. `education-period-ended`는 문구에 **종료일**이 들어가야 한다 — 「기간이
 * 끝났다」만으로는 *언제 끝났는지* 를 모르고, 그러면 읽는 사람이 설정을 다시 열어 확인해야 한다.
 *
 * 상수 맵을 유지한 채 호출부에서 날짜를 이어붙일 수도 있었지만 그러면 **문구의 소유가 갈린다**
 * (한 상태의 문장이 lib과 화면에 반씩 살게 된다). 이 모듈의 존재 이유가 *두 자리가 각자 말하지
 * 않게 하는 것*이라 그쪽이 더 나쁘다.
 *
 * `Record`가 하던 일(**상태를 늘리면 tsc가 미기재를 거부**)은 `switch` + `const exhaustive: never`
 * 가 그대로 한다 — 선례는 rc `missingRequiredToastMessage`다. 승격의 목적이 바로 그 보호를 잃지
 * 않는 것이다.
 */
export function tbmConductorSignatureNotice(
  state: TbmConductorSignatureState,
  opts: { educationEndDate?: string | Date | null },
): string {
  switch (state) {
    case 'offline':
      return '교육 담당자 확인 서명은 출력물에 직접 받습니다.';
    case 'awaiting-educator':
      return '교육 담당자가 아직 교육을 진행하지 않아 교육 담당자 확인 서명란이 비어 있습니다.';
    // 주어를 넣지 않는다 — 이 문서를 여는 사람이 곧 진행할 본인이라, "담당자가"도 "직접
    // 진행하실"도 군더더기다. 남는 사실은 *교육이 아직 안 열렸다*뿐이다.
    case 'awaiting-self':
      return '아직 교육을 진행하지 않아 교육 담당자 확인 서명란이 비어 있습니다.';
    /**
     * 위 셋과 달리 **두 문장**이다 — 사유 한 줄에 「지금 확정하면」을 붙이는 이유:
     *
     * 되돌릴 수 없는 대가는 **결정 직전에만 말하면 늦다.** 모달 경고는 확정 버튼을 누른 뒤에야
     * 보이는데, 그때 사용자가 할 수 있는 것은 취소뿐이다. 이 줄은 상세를 열자마자 보이므로
     * *담당자에게 마무리를 재촉한다* 같은 **다른 행동**을 할 여지를 준다. 나머지 셋은 대가가
     * 없거나(오프라인) 되돌릴 수 있어(교육을 나중에 열면 된다) 사유만으로 충분했다.
     */
    case 'education-unfinished':
      return '교육이 아직 진행 중이라 교육 담당자 확인 서명란이 비어 있습니다. 지금 확정하면 진행 중인 회차를 마무리할 수 없습니다.';
    /**
     * 같은 「진행 중」이지만 **말하는 사실이 다르다.** 위 문구의 「마무리할 수 없습니다」는 이
     * 문서에 대해 거짓이다 — 마무리할 회차 자체가 확정과 함께 사라진다.
     *
     * 그래서 대가가 아니라 **일어날 일**을 말한다. 위 셋과 달리 이쪽은 사용자가 막아야 할 손실이
     * 없으므로(빈 회차엔 지켜야 할 참석 사실이 없다) 재촉할 다른 행동도 없다 — 두 문장으로 늘릴
     * 이유가 없고, 한 문장이 정확하면 그것이 낫다.
     *
     * 「비어 있는」이라 쓰고 「내용이 없는」이라 쓰지 않는다: 사용자가 화면에서 보는 것이 *빈
     * 참석자 표와 빈 확인란*이라 그 어휘로 가리켜야 어느 회차인지 찾을 수 있다.
     */
    case 'education-unfinished-empty':
      return '진행 중인 교육 회차가 비어 있어 교육 담당자 확인 서명란이 비어 있습니다. 확정하면 그 회차는 문서에서 제외됩니다.';
    /**
     * `education-unfinished`와 **같은 대가, 다른 어휘**(F-55). 활동 배지가 「교육 준비 중」이라 말하는
     * 문서에 「교육이 진행 중이라」로 답하면 한 화면이 두 말을 한다. 괄호 안 한 마디는 「준비 중」이
     * 무엇인지(담당자가 입력은 시작했지만 「교육 시작」은 아직) 처음 보는 사람을 위한 것이다.
     */
    case 'education-preparing':
      return '교육이 아직 준비 중이라(담당자가 입력을 시작했지만 교육 시작 전) 교육 담당자 확인 서명란이 비어 있습니다. 지금 확정하면 준비 중인 회차를 마무리할 수 없습니다.';
    /**
     * **날짜를 말한다**(2026-09-30). 위 여섯과 달리 이 상태의 문장은 *언제* 없이는 반쪽이다 —
     * 「기간이 종료되어」만 읽으면 설정 화면을 다시 열어 종료일을 확인하게 되고, 그 왕복이
     * 이 한 줄이 없애려던 바로 그 수고다.
     *
     * 종료일 포맷은 **`toEndDateLabel`이 KST 기준으로** 만든다(그 근거는 그쪽 JSDoc이 소유한다).
     * 경계의 «뜻»은 저장 규약이 정하며 그 표는 shared `isTbmEducationPeriodEnded`에 있다 — 두
     * 세대(구 = UTC 자정 = 종료일 09:00 / 신 = KST 23:59:59.999)가 있으니 **「종료일 당일은 언제나
     * 열려 있다」고 읽지 말 것**.
     *
     * 「아직」을 쓰지 않는다 — 위 다섯과 갈리는 지점이 정확히 거기다. 그 말은 *이제부터 일어날
     * 수 있다*는 약속인데 이 문서에서는 거짓이다. 주어도 넣지 않는다: 기간이 끝나면 담당자도
     * 작성자 본인도 진행할 수 없어(F-36) 가를 것이 없다.
     *
     * 날짜를 **모르면**(`null`·파싱 불가) 날짜 없는 문장으로 떨어진다. 그 상태는 이 판정이
     * `educationEndDate`를 보고 내려진 것이라 실질적으로 도달하지 않지만, 지어낸 날짜를
     * 적는 것보다는 한 마디 덜 말하는 쪽이 언제나 맞다.
     */
    case 'education-period-ended': {
      const endLabel = toEndDateLabel(opts.educationEndDate);
      return endLabel
        ? `교육 기간이 종료(${endLabel})되어 교육 담당자 확인 서명란이 비어 있습니다.`
        : '교육 기간이 종료되어 교육 담당자 확인 서명란이 비어 있습니다.';
    }
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * 종료일 표시 라벨 `'YYYY-MM-DD'` — **KST 기준**으로 포맷한다(shared `formatDateISO`).
 *
 * ## 왜 ISO 앞 10자 슬라이스가 아닌가
 * 이 앱의 표시 규약이 **KST**이고(CLAUDE.md 시간대 정책: 내부는 UTC, 표시는 로컬), 그 정본
 * 유틸이 `formatDateISO`(`'sv-SE'` + `Asia/Seoul` → `YYYY-MM-DD`)다. 표시용 날짜를 손으로 자르는
 * 자리를 하나 더 만들지 않는다.
 *
 * ⚠️ **지금은 두 방식의 «결과»가 같다** — 그래서 이건 버그 수정이 아니라 규약 정렬이다
 * (2026-09-30 계산 확인, 저장 두 세대 × 네 날짜 전부 일치):
 *
 * | 세대 | 저장값 | UTC 슬라이스 | KST 포맷 |
 * |---|---|---|---|
 * | 구(UTC 자정) | `2026-08-28T00:00:00Z` | `2026-08-28` | `2026-08-28` |
 * | 신(KST 23:59:59.999) | `2026-08-28T14:59:59.999Z` | `2026-08-28` | `2026-08-28` |
 *
 * 다만 신 형상 값은 UTC `14:59:59.999`로, 두 방식이 갈리는 **`15:00:00Z` 절벽에서 1ms 아래**다
 * (`15:00:00Z`면 UTC는 `08-28`·KST는 `08-29`). 쓰기 규약이 조금만 움직여도 표시가 하루 밀리는
 * 자리라, 1ms에 기대지 않고 표시 축을 명시한다.
 *
 * ⚠️ **위 표는 «종료일»만 다룬다 — 시작일은 오늘도 이미 밀린다.** `educationPeriodStartAt`은
 * 대칭이 아니라 **KST 자정**으로 저장하므로(`2026-08-28` → `2026-08-27T15:00:00Z`) ISO 앞 10자
 * 슬라이스는 **전날**(`2026-08-27`)을 준다. 즉 시작일 라벨을 만들 때 슬라이스를 쓰면 1ms 절벽이
 * 아니라 **평상시에 틀린다.** 그래서 이 모듈의 날짜 라벨은 예외 없이 `formatDateISO`(KST)를 쓴다 —
 * 양끝의 저장 규약이 다르다는 사실에 표시가 의존하지 않게 하는 것이 이 선택의 진짜 값이다.
 * (그 규약 불일치 자체는 별 줄기다:
 *  `docs/work-streams/backlog/education-period-multisession-axis-convention-mismatch.md`.)
 *
 * 경계 자체의 뜻은 저장 규약이 정한다 — shared `isTbmEducationPeriodEnded` JSDoc 참조.
 */
function toEndDateLabel(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : formatDateISO(date);
}

/**
 * 완료 확인 모달의 경고 — **상태별 문구 맵**. `null`이면 그 상태에는 경고하지 않는다.
 *
 * ## 목록에서 맵으로 (2026-09-09)
 * 종전엔 `AWAITING_EDUCATION_STATES`라는 **목록의 소속**으로 「경고 대상인가」를 답했고,
 * 그 규약의 이유는 *접두(`awaiting-`) 비교로 판정하면 상태가 늘 때 조용히 따라 들어온다* 였다.
 * `education-unfinished`가 그 구조를 넘어선다 — **경고는 해야 하는데 문구가 달라야** 하기
 * 때문이다(「아직 교육을 진행한 기록이 없습니다」는 그 문서에 대해 **거짓**이다. 기록은 있다).
 * 불리언 소속으로는 「경고한다」와 「무엇이라 경고한다」를 함께 답할 수 없다.
 *
 * 규약의 취지는 그대로 지킨다 — 오히려 강해진다: `Record<TbmConductorSignatureState, …>`는
 * 상태를 추가하면 **`tsc`가 미기재를 거부**하므로, 조용히 따라 들어오는 일이 리뷰 규율이
 * 아니라 타입으로 막힌다. 접두 비교를 금지하던 자리를 컴파일러가 대신 선다.
 *
 * ## 왜 여전히 «경고»이고 «차단»이 아닌가
 * 오프라인은 종전 그대로 `null`이다(세션 개념이 없어 매번 뜨는 경고가 되고, 매번 뜨는 경고는
 * 곧 아무도 읽지 않는다).
 *
 * ⚠️ **`education-unfinished`를 차단으로 올리지 않는 근거는 종전 것과 다르다.** 종전 근거는
 * *안전관리자가 오프라인 진행분을 대신 마감하는 정상 흐름이 있다* 였는데, 그건 교육 0건
 * 상태의 이야기라 여기엔 그대로 옮겨오지 않는다. 이 상태를 차단하면 안 되는 이유는 따로 있고
 * 더 강하다: **`IN_PROGRESS`는 스스로 풀리지 않는 래치다.** 서버 어디에도 세션을 자동으로
 * 닫는 경로가 없고(닫는 것은 `tbm.complete()` 하나뿐), 세션 재사용 조건이 `IN_PROGRESS`
 * 하나라 그날 완료 후 다시 시작하면 또 생긴다. 차단으로 만들면 **버려진 회차 하나가 문서를
 * 영영 확정 불가로 못박는다** — 서명란이 비는 것보다 훨씬 나쁜 실패다. 그래서 대가를 정확히
 * 말하고 결정은 사람에게 남긴다.
 *
 * ## 문구 규약
 * 주어를 넣지 않아 **직접 진행 건에도 그대로 참이다**(chrome 한 줄과 달리 가를 것이 없다).
 * 동사(완료/제출)도 넣지 않는다 — 이 문서의 동사는 결재 유무에 따라 갈리고 그 축은 호스트가
 * 쥔 값이다. 그래서 `education-unfinished` 문구도 「확정」이라는 **중립 상위어**를 쓴다(이
 * 코드베이스에서 확정 = 제출·완료를 함께 덮는 말이다 — shared `isTbmFinalizedDocument`).
 */
const CONDUCTOR_COMPLETION_WARNINGS: Record<TbmConductorSignatureState, string | null> = {
  offline: null,
  'awaiting-educator': '아직 교육을 진행한 기록이 없습니다 — 교육 담당자 확인 서명란이 빈 채로 남습니다.',
  'awaiting-self': '아직 교육을 진행한 기록이 없습니다 — 교육 담당자 확인 서명란이 빈 채로 남습니다.',
  // F-7 이후의 사실을 말한다 — 「빈 채로 남는다」에서 그치면 되돌릴 수 없다는 것이 안 보인다.
  'education-unfinished':
    '교육이 아직 진행 중입니다 — 지금 확정하면 진행 중인 회차를 마무리할 수 없고, 교육 담당자 확인 서명란이 빈 채로 남습니다.',
  /**
   * 여전히 **경고한다** — 되돌릴 수 없는 삭제이기 때문이다(결정 2026-09-09). 「잃을 데이터가
   * 정의상 0」이라는 근거가 옳더라도, 사라진다는 사실은 누르기 전에 말해야 한다. 다만 위 넷과
   * 달리 「빈 채로 남습니다」를 붙이지 않는다: 그 회차가 없어지므로 **남을 빈칸도 없다**.
   * 그 말을 붙이면 문서에 빈 부가 그대로 있을 것처럼 읽혀, 정작 일어나는 일(제외)을 가린다.
   */
  'education-unfinished-empty':
    '진행 중인 교육 회차가 비어 있습니다 — 확정하면 그 회차는 문서에서 제외되며, 되돌릴 수 없습니다.',
  // F-55: `education-unfinished`와 같은 대가를 「준비 중」 어휘로 — 배지와 같은 말을 쓴다.
  'education-preparing':
    '교육이 아직 준비 중입니다(입력은 시작됐지만 교육 시작 전) — 지금 확정하면 준비 중인 회차를 마무리할 수 없고, 교육 담당자 확인 서명란이 빈 채로 남습니다.',
  /**
   * **여전히 경고하되 「지금 확정하면」을 붙이지 않는다**(2026-09-30).
   *
   * 위 넷의 두 번째 절은 *확정을 미루면 달라질 수 있다*는 뜻을 담는다 — 담당자를 재촉하거나
   * 회차를 마치면 서명란이 채워지니까. 만료 건에는 **미뤄서 달라질 것이 없다**: 새 회차를 열
   * 수 없고(F-36) 링크 재전송도 거부된다. 그 문장을 그대로 두면 *기다리면 채워질 수 있다*는
   * 거짓 여지를 만든다.
   *
   * 그렇다고 `null`(경고 안 함)로 두지 않는다 — 서명란이 **영구히 비는 문서**를 확정하는 것은
   * 그 자체로 알려야 할 사실이고, 오프라인처럼 *종이가 정본*인 대안이 있는 것도 아니다.
   * 남는 사실 둘(왜 못 채우나 · 그 결과)만 적는다.
   *
   * 여기서는 날짜를 말하지 않는다 — 모달은 상세 한 줄을 이미 본 사람이 여는 자리라 *언제*는
   * 그 줄이 답했고, 이 시점의 질문은 *눌러도 되는가* 하나다.
   */
  'education-period-ended':
    '교육 기간이 종료되어 더 진행할 수 없습니다 — 교육 담당자 확인 서명란이 빈 채로 남습니다.',
};

/** 완료 확인 모달의 경고 문구. 경고할 상황이 아니면 `null`. */
export function tbmConductorCompletionWarning(
  report: Pick<
    Report,
    | 'tbmSignatureMode'
    | 'educationSessionCount'
    | 'tbmSelfConducted'
    | 'educationSessions'
    | 'educationEndDate'
  >,
  now?: Date,
): string | null {
  const state = deriveTbmConductorSignatureState(report, now);
  return state ? CONDUCTOR_COMPLETION_WARNINGS[state] : null;
}

/**
 * ## 확정된 뒤에는 «독자»가 바뀐다 (2026-09-09)
 *
 * 위 문구들은 전부 **확정을 누르려는 사람**의 것이다 — `education-unfinished`가 「**지금 확정하면**
 * 진행 중인 회차를 마무리할 수 없습니다」라고 말하는 데서 그게 드러난다. 그런데 그 문장이 실제로
 * 읽히는 자리 하나는 **이미 확정된 문서**다(결재 대기·완료본을 여는 화면). 거기서는 이미 지난
 * 얘기라, 정작 그 문서를 승인할지 정하는 사람에게는 **아무 말도 하지 않는 것과 같다.**
 *
 * ## 축은 «사람»이 아니라 «문서 상태»다
 * 역할(작성자/결재자)로 가르고 싶어지지만 그건 틀린 축이다 — `PENDING_APPROVAL` 문서를 **작성자가
 * 봐도** 「지금 확정하면」은 똑같이 거짓이다. 문장의 참·거짓을 정하는 것은 *누가 보느냐*가 아니라
 * *확정이 이미 일어났느냐*이므로, 갈래는 `isTbmFinalizedDocument`(shared) 하나로 가른다. 외부
 * 결재자는 확정본만 볼 수 있으므로 이 축이 그쪽을 **자동으로** 덮는다.
 *
 * shared `tbmAuthorEducationWriteBlockReason`이 같은 판정 위에 `overrides.finalized`를 얹어 문구만
 * 가르는 것과 같은 형태다 — 수렴시킨 것은 «판정»이지 «문구 한 벌»이 아니다.
 *
 * ## 판정은 문서 본문의 「미완료」 표식과 **같은 술어**다
 * `isUnfinishedTbmEducationRound`(문서 확정 ∧ 회차 `IN_PROGRESS`)는 서버가 담당자 확인 칸에
 * `educatorUnfinishedMark`를 박을 때 쓰는 그 술어다. 화면이 자기 술어를 따로 지으면 *칸에는
 * 표식이 있는데 위 안내는 침묵하는*(또는 그 반대) 비대칭이 생긴다 — F-7이 고친 병 그대로다.
 *
 * ## `isEmpty`를 묻지 않는 이유
 * 확정 시 **빈** 진행 중 회차는 서버가 지운다(결정 2026-09-09 안 D). 확정본에 남아 이 술어가
 * 참이 되는 회차는 실질적으로 내용 있는 것이고, 삭제가 안 돈 옛 문서에서 빈 회차가 남았더라도
 * 그 칸은 **정말로 영원히 비므로** 안내가 옳다. 덕분에 외부 결재 응답이 `isEmpty`를 안 실어도
 * (`external-report-view.helper.ts`가 일부러 뺀다) 이 안내는 그대로 성립한다.
 */
export interface TbmUnfinishedRoundNoticeInput {
  /**
   * 문서 축(`Report.status`).
   *
   * ⚠️ shared 술어는 `TbmDocumentStatus`로 좁게 받는데 **여기서만 넓게 받는다** — 외부 결재
   * 응답의 계약이 `ExternalSignatureReportView.status: string`이라 그 자리에서 좁힐 방법이
   * 캐스트뿐이기 때문이다. 좁은 타입이 막으려던 실수(세션 상태를 문서 자리에 넣는 것)는 여기서는
   * 구조가 막는다 — 세션 축은 이름도 다르고(`sessionStatuses`) **배열**이라 바꿔 넣을 수 없다.
   */
  documentStatus: TbmDocumentStatus | (string & {}) | null | undefined;
  /**
   * 활동 축 — 회차 행의 `status`만 뽑아 넘긴다. `undefined`면 **아무 주장도 하지 않는다**(목록·옛
   * 응답에는 행이 없다 — 이 모듈의 기존 「모르는 것은 0이 아니다」 계약 그대로).
   */
  sessionStatuses: readonly string[] | null | undefined;
}

/**
 * 확정본을 읽는 사람에게 **미완료로 박제된 회차가 몇 건인지** 말한다. 해당 없으면 `null`.
 *
 * ## 왜 개수를 말하나
 * 결재자는 문서를 넘기며 본다. 「미완료 회차가 있습니다」만으로는 *어디까지 찾아봐야 하나*를
 * 모르고, 회차 부(部)에는 아직 번호 라벨이 없어(결정 2026-09-09 「별건」) 눈으로 세기도 어렵다.
 *
 * ## 왜 「칸에 「미완료」가 표시됩니다」라고 쓰지 않나
 * 그 표식은 **스냅샷에 그 바인딩이 있는 문서**에만 붙는다(결정 2026-09-09 후속 「적용 범위가
 * 둘로 갈린다」) — 그 전에 만들어진 확정본에는 값이 있어도 그릴 자리가 없다. 그러니 본문이
 * 무엇을 그리는지는 주장하지 않고, **어느 세대에서도 참인 사실**(그 칸은 더 채워지지 않는다)만
 * 말한다. 화면이 본문에 없는 것을 있다고 하면 읽는 사람이 그것을 찾다가 문서를 의심한다.
 *
 * ## 왜 「확정된 문서입니다」가 아닌가
 * `isTbmFinalizedDocument`는 `CANCELLED`도 참으로 친다(폐기본도 담당자가 더 못 쓴다). 폐기본을
 * 「확정된 문서」라 부르면 거짓이라, 동사를 빼고 남은 사실만 적는다.
 */
export function tbmUnfinishedRoundNotice(
  input: TbmUnfinishedRoundNoticeInput,
): string | null {
  if (!input.sessionStatuses) return null;
  const count = input.sessionStatuses.filter((sessionStatus) =>
    isUnfinishedTbmEducationRound({
      documentStatus: input.documentStatus as TbmDocumentStatus | null | undefined,
      sessionStatus,
    }),
  ).length;
  if (count === 0) return null;
  return `교육 회차 ${count}건이 미완료로 남았습니다 — 그 회차의 교육 담당자 확인 서명란은 더 채워지지 않습니다.`;
}

/*
 * (제거) `tbmEducationSessionStatuses` — 느슨한 `educationSessions?: unknown[]`에서 상태만 뽑던
 * 어댑터. 그 필요는 **서버 계약이 틀려서** 생긴 것이었고(F-11: 그 필드는 레거시 TBM 게이트 안이라
 * COMPOSED 실문서에 애초에 안 실렸다), 계약을 `educationSessionStatuses?: string[]`로 바로잡자
 * 어댑터가 지킬 것이 없어졌다. **「모르는 것과 없는 것」의 구분은 위 함수가 그대로 갖는다** —
 * `sessionStatuses`가 `undefined`면 아무 주장도 하지 않는다.
 */
